import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { shellTokens } from '../shell/theme/shellTheme';
import HeartbeatPanel from './components/HeartbeatPanel';
import TelemetryFeed from './components/TelemetryFeed';
import { useMergedTelemetryFeed } from './hooks/useMergedTelemetryFeed';

const filterSx = {
    minWidth: 220,
    '& .MuiOutlinedInput-root': {
        borderRadius: `${shellTokens.innerRadius}px`,
        fontSize: '0.82rem',
        bgcolor: shellTokens.surface,
    },
};

const StrategyTelemetryPage = () => {
    const [strategyFilter, setStrategyFilter] = useState('');
    const { data, error, isLoading, isFetching, refetch } = useMergedTelemetryFeed({
        strategyId: strategyFilter || null,
    });

    const heartbeats = useMemo(() => data?.heartbeats || [], [data?.heartbeats]);
    const events = useMemo(() => data?.events || [], [data?.events]);
    const summary = data?.summary;
    const pagination = data?.pagination;

    const strategyOptions = useMemo(() => {
        if (data?.strategies?.length) {
            return data.strategies;
        }
        const map = new Map();
        heartbeats.forEach((row) => {
            if (!map.has(row.strategyId)) {
                map.set(row.strategyId, row.strategyLabel || row.strategyId);
            }
        });
        return Array.from(map.entries()).map(([strategyId, strategyLabel]) => ({
            strategyId,
            strategyLabel,
        }));
    }, [data?.strategies, heartbeats]);

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                sx={{ mb: 1.75 }}
            >
                <TextField
                    select
                    size="small"
                    label="Strategy filter"
                    value={strategyFilter}
                    onChange={(event) => setStrategyFilter(event.target.value)}
                    sx={filterSx}
                >
                    <MenuItem value="">All strategies</MenuItem>
                    {strategyOptions.map((option) => (
                        <MenuItem key={option.strategyId} value={option.strategyId}>
                            {option.strategyLabel}
                        </MenuItem>
                    ))}
                </TextField>

                {isFetching && !isLoading ? (
                    <Typography sx={{ fontSize: '0.72rem', color: shellTokens.muted }}>
                        Refreshing telemetry…
                    </Typography>
                ) : null}
            </Stack>

            {error ? (
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                    sx={{ mb: 1.75 }}
                >
                    Telemetry feed unavailable — {error.message}
                </Alert>
            ) : null}

            <HeartbeatPanel heartbeats={heartbeats} summary={summary} loading={isLoading} />

            <TelemetryFeed
                events={events}
                loading={isLoading}
                pagination={pagination}
            />
        </Box>
    );
};

export default StrategyTelemetryPage;
